/**
 * Replay executors — the actual Supabase writes for each queued op kind.
 *
 * These are deliberately framework-free (no React, no query invalidation):
 * they just perform the write and throw on failure. Cache invalidation after
 * a successful sync is handled in the React layer (OfflineProvider) via the
 * queue's "synced" event, which keeps these pure and avoids import cycles.
 *
 * The check-in chain (checkin → break_toggle → checkout → submit_log) is
 * stateful: an offline check-in has no server id yet, so its dependents carry
 * a "local:uuid" reference that's resolved to the real check_ins.id — recorded
 * in the idMap when the check-in syncs — at replay time.
 */
import { supabase } from "@/integrations/supabase/client";
import { getIdMap, putIdMap } from "./db";
import type { OpKind, QueueItem } from "./types";

export type QueueExecutor = (
  payload: Record<string, unknown>,
  item: QueueItem,
) => Promise<void>;

/** Thrown by a dependent op whose check-in hasn't synced yet. Retryable: the
 *  next flush (after the check-in syncs) will resolve the reference. */
export class DependencyPendingError extends Error {
  readonly retryable = true;
  constructor() {
    super("Waiting for the linked check-in to sync first.");
    this.name = "DependencyPendingError";
  }
}

/**
 * Classify a write failure: keep the item queued and retry, or has it
 * permanently failed?
 *
 * Network failures (fetch never reached the server) → retry. Errors that came
 * back FROM the server — Postgres constraint violations, RLS denials, bad
 * input — are permanent; retrying would loop forever. Transient server / auth
 * codes (5xx, 408, 429, 401, 403) are retryable, as is a pending dependency.
 */
export function isRetryableError(err: unknown): boolean {
  if (!err) return true;
  if ((err as { retryable?: boolean }).retryable) return true; // DependencyPending
  if (err instanceof TypeError) return true; // fetch threw → offline/network

  const e = err as { status?: number; code?: string; message?: string };

  // A Postgres error code (e.g. "23505" unique, "23502" not-null, "42501" RLS)
  // means the request reached the database and was rejected deterministically.
  if (typeof e.code === "string" && /^[0-9A-Z]{5}$/.test(e.code)) return false;

  const msg = (e.message ?? "").toLowerCase();
  if (/failed to fetch|networkerror|network error|load failed|timeout|timed out/.test(msg)) {
    return true;
  }

  const status = typeof e.status === "number" ? e.status : undefined;
  if (status === undefined) {
    // No status and no PG code → couldn't classify; treat as transient. The
    // queue's MAX_ATTEMPTS cap prevents an infinite loop on a truly stuck item.
    return true;
  }
  if (status === 408 || status === 429 || status === 401 || status === 403) return true;
  if (status >= 500) return true;
  return false; // other 4xx → permanent
}

/** Resolve a check-in reference to a real check_ins.id. A "local:" reference
 *  must be looked up in the idMap; if it's not there yet, the check-in hasn't
 *  synced and we throw (retryable). A bare id is already real. */
async function resolveCheckInId(ref: string): Promise<string> {
  if (!ref.startsWith("local:")) return ref;
  const real = await getIdMap(ref);
  if (!real) throw new DependencyPendingError();
  return real;
}

// ---- payload shapes --------------------------------------------------------
export interface MetricEntryPayload {
  definitionId: string;
  activationId: string;
  value: number;
  entryDate: string; // local YYYY-MM-DD captured at action time
  staffId: string;
}
export interface CheckinPayload {
  localCheckInId: string;
  staffId: string;
  activationId: string;
  checkInAt: string;
  lat: number;
  lng: number;
  organizationId: string | null;
}
export interface BreakTogglePayload {
  checkInRef: string;
  breakStartAt: string | null;
  totalBreakMinutes: number;
  status: "active" | "on_break";
}
export interface CheckoutPayload {
  checkInRef: string;
  checkOutAt: string;
  lat: number;
  lng: number;
  /** Local YYYY-MM-DD captured at checkout — the server never derives it (TZ). */
  logDate: string;
  notes?: string;
}
export interface SubmitLogPayload {
  checkInRef: string;
  hours: number;
  notes: string | null;
  logDate: string;
}
export interface GeofenceViolationPayload {
  staffId: string;
  activationId: string;
  lat: number;
  lng: number;
  distanceMeters: number;
  geofenceRadiusMeters: number;
  organizationId: string | null;
}
export interface CheckinPhotoPayload {
  /** Client-generated uuid — doubles as the photos row id AND the storage
   *  filename, so replays are idempotent end to end. */
  photoId: string;
  checkInRef: string;
  staffId: string;
  activationId: string;
  organizationId: string | null;
  capturedAt: string;
  /** Compressed image blob. Survives IndexedDB via structured clone. */
  blob: Blob;
  /** The blob's real mime type — the bucket allowlist checks the declared type. */
  contentType: string;
}

export const executors: Partial<Record<OpKind, QueueExecutor>> = {
  metric_entry: async (payload) => {
    const p = payload as unknown as MetricEntryPayload;
    const { error } = await supabase.from("activation_metric_entries").insert({
      definition_id: p.definitionId,
      activation_id: p.activationId,
      value: p.value,
      entry_date: p.entryDate,
      staff_id: p.staffId,
    });
    if (error) throw error;
  },

  checkin: async (payload) => {
    const p = payload as unknown as CheckinPayload;
    const { data, error } = await supabase
      .from("check_ins")
      .insert({
        staff_id: p.staffId,
        activation_id: p.activationId,
        check_in_at: p.checkInAt,
        check_in_lat: p.lat,
        check_in_lng: p.lng,
        organization_id: p.organizationId,
      })
      .select("id")
      .single();
    if (error) throw error;
    // Record local → real so dependents can resolve their target row.
    await putIdMap(p.localCheckInId, (data as { id: string }).id);
  },

  break_toggle: async (payload) => {
    const p = payload as unknown as BreakTogglePayload;
    const id = await resolveCheckInId(p.checkInRef);
    const { error } = await supabase
      .from("check_ins")
      .update({
        break_start_at: p.breakStartAt,
        total_break_minutes: p.totalBreakMinutes,
        status: p.status,
      })
      .eq("id", id);
    if (error) throw error;
  },

  checkout: async (payload) => {
    const p = payload as unknown as CheckoutPayload;
    const id = await resolveCheckInId(p.checkInRef);
    // Atomic + idempotent: creates the PENDING time_log and closes the check-in
    // in one transaction (checkout_shift RPC), so hours are captured the instant
    // the shift ends — even if the phone dies before "Submit". Safe to retry:
    // re-running reuses the existing time_log_id, never double-logs.
    const { error } = await supabase.rpc("checkout_shift", {
      _check_in_id: id,
      _end_at: p.checkOutAt,
      _lat: p.lat,
      _lng: p.lng,
      _log_date: p.logDate,
      _notes: p.notes ?? undefined,
    });
    if (error) throw error;
  },

  submit_log: async (payload) => {
    const p = payload as unknown as SubmitLogPayload;
    const id = await resolveCheckInId(p.checkInRef);
    // Adjust the pending log's hours/notes and mark the check-in submitted, in
    // one idempotent RPC. It tolerates an already-approved log (no-op) and
    // back-creates the log if one is somehow missing (legacy completed shift).
    const { error } = await supabase.rpc("submit_shift_hours", {
      _check_in_id: id,
      _hours: p.hours,
      _notes: p.notes ?? undefined,
      _log_date: p.logDate,
    });
    if (error) throw error;
  },

  geofence_violation: async (payload) => {
    const p = payload as unknown as GeofenceViolationPayload;
    const { error } = await supabase.from("geofence_violations").insert({
      staff_id: p.staffId,
      activation_id: p.activationId,
      latitude: p.lat,
      longitude: p.lng,
      distance_meters: p.distanceMeters,
      geofence_radius_meters: p.geofenceRadiusMeters,
      organization_id: p.organizationId,
    });
    if (error) throw error;
  },

  checkin_photo: async (payload) => {
    const p = payload as unknown as CheckinPhotoPayload;
    const checkInId = await resolveCheckInId(p.checkInRef);

    // 1. Upload the blob — WITHOUT upsert, treating "already exists" as
    //    success. The bucket has no UPDATE policy (objects are immutable by
    //    design), so an upsert overwrite would be RLS-denied on the retry
    //    after a partial success (uploaded, then the row insert failed).
    //    The filename is the client-generated photoId, so a duplicate can
    //    only ever be our own earlier attempt.
    const path = `${p.organizationId ?? "no-org"}/${p.activationId}/${p.photoId}.jpg`;
    const { error: uploadErr } = await supabase.storage
      .from("check-in-photos")
      .upload(path, p.blob, { contentType: p.contentType || "image/jpeg", upsert: false });
    if (uploadErr) {
      const dup =
        (uploadErr as { statusCode?: string | number; error?: string; message?: string });
      const isDuplicate =
        String(dup.statusCode ?? "") === "409" ||
        /duplicate|already exists/i.test(dup.error ?? "") ||
        /duplicate|already exists/i.test(dup.message ?? "");
      if (!isDuplicate) throw uploadErr;
    }

    // 2. Link it. Client-generated id + ignoreDuplicates makes replay a no-op.
    const { error: rowErr } = await supabase.from("check_in_photos").upsert(
      {
        id: p.photoId,
        check_in_id: checkInId,
        staff_id: p.staffId,
        activation_id: p.activationId,
        organization_id: p.organizationId,
        file_path: path,
        kind: "check_in",
        captured_at: p.capturedAt,
      },
      { onConflict: "id", ignoreDuplicates: true },
    );
    if (rowErr) throw rowErr;
  },
};
